import { useState } from 'react';
import { Check, Clock, Sparkles } from 'lucide-react';
import type { SuggestionBatch } from '@4zen/api-client';
import { Badge, Button, PriorityBadge, cn } from '@4zen/ui';
import { useToast } from '@/components/Toast';
import { formatDuration } from '@/lib/format';
import { useAcceptSuggestion, useDismissSuggestions } from './queries';

/**
 * Shown once every task planned for today is closed and working time is left.
 *
 * The point of the prompt is that the user chooses. The system ranks the
 * backlog against the time actually remaining and the priority, then steps
 * back — it never silently drags tomorrow's work into today.
 */
export function SuggestionPrompt({ batch }: { batch: SuggestionBatch }) {
  const toast = useToast();
  const accept = useAcceptSuggestion();
  const dismiss = useDismissSuggestions();
  const [selected, setSelected] = useState<string | null>(batch.items[0]?.task_id ?? null);

  if (batch.status !== 'open' || batch.items.length === 0) return null;

  return (
    <section className="rounded border border-accent/40 bg-accent-soft p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold">Усе заплановане на сьогодні виконано</h2>
        <span className="text-sm text-content-muted">
          Лишилось {formatDuration(batch.remaining_minutes)}. Візьмемо щось ще?
        </span>
      </header>

      <div className="grid gap-2 md:grid-cols-3">
        {batch.items.map((suggestion) => {
          const isSelected = selected === suggestion.task_id;
          return (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => setSelected(suggestion.task_id)}
              className={cn(
                'flex flex-col gap-2 rounded border bg-surface p-3 text-left transition-colors',
                isSelected
                  ? 'border-accent ring-1 ring-accent'
                  : 'border-line hover:border-line-strong',
              )}
            >
              <div className="flex items-start gap-2">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full border border-line text-[11px] font-semibold text-content-muted">
                  {suggestion.rank}
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium">{suggestion.task.title}</span>
                {isSelected ? <Check className="h-4 w-4 shrink-0 text-accent" /> : null}
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <PriorityBadge priority={suggestion.task.priority} />
                <Badge tone={suggestion.fits_remaining_time ? 'success' : 'warning'}>
                  <Clock className="mr-1 h-3 w-3" />
                  {formatDuration(suggestion.estimate_minutes)}
                </Badge>
                {suggestion.task.client_name ? (
                  <Badge tone="neutral">{suggestion.task.client_name}</Badge>
                ) : null}
              </div>

              <p className="text-xs text-content-subtle">{suggestion.rationale}</p>
            </button>
          );
        })}
      </div>

      <footer className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          disabled={!selected}
          loading={accept.isPending}
          onClick={async () => {
            if (!selected) return;
            await accept.mutateAsync({ batchId: batch.id, taskId: selected });
            toast.success('Задачу додано в сьогоднішній план');
          }}
        >
          Взяти в роботу
        </Button>
        <Button
          variant="ghost"
          loading={dismiss.isPending}
          onClick={async () => {
            await dismiss.mutateAsync({ batchId: batch.id, reason: 'enough_for_today' });
            toast.success('Добре, на сьогодні достатньо');
          }}
        >
          На сьогодні достатньо
        </Button>
        <span className="ml-auto text-xs text-content-subtle">
          Обрано з {batch.candidate_pool_size} відкритих задач
        </span>
      </footer>
    </section>
  );
}
